import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import * as _ from 'lodash';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { concatMap, map, mergeMap } from 'rxjs/operators';
import { BaseCancellationComponent } from '../../components/base-cancellation/base-cancellation.component';
import {
    CancellationComponentInstanceToken,
    CancellationComponentInterface,
} from '../../components/cancellation/cancellation-component.instance';
import { CancellationModel } from '../../components/cancellation/cancellation.model';
import { ConfigConstants } from '../../config/config-constants';
import { InitializableInterface } from '../../interfaces/initializable.interface';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import { NotificationUpdateModel } from '../../models/low-level/notification-update.model';
import { NotificationIdentifier } from '../../types/notification-identifier';
import { NotificationDataInstanceToken, NotificationDataInterface } from '../../interfaces/notification-data.interface';
import { NotificationModel } from '../../models/low-level/notification.model';
import { EventService } from '../event/event.service';
import { RouterHelperService } from '../router-helper/router-helper.service';
import { LocalStorageNotificationUpdateService } from './local-storage-notification-update.service';

@Injectable({
    providedIn: 'root',
})
export class NotificationUpdateService implements InitializableInterface {
    private notificationUpdates: NotificationUpdateModel[];
    private newUpdatesSyncCompletedSubject: BehaviorSubject<NotificationUpdateModel[]>;
    public newUpdatesSyncCompleted$: Observable<NotificationUpdateModel[]>;

    public isInitialized = false;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly modalService: BsModalService,
        private readonly routerHelper: RouterHelperService,
        private readonly router: Router,
        private readonly eventService: EventService,
        @Inject(NotificationDataInstanceToken) private readonly notificationService: NotificationDataInterface,
        private readonly localStorageNotificationUpdateService: LocalStorageNotificationUpdateService,
        @Inject(CancellationComponentInstanceToken) private readonly cancellationComponent: BaseCancellationComponent,
    ) {
        this.newUpdatesSyncCompletedSubject = new BehaviorSubject<NotificationUpdateModel[]>([]);
        this.newUpdatesSyncCompleted$ = this.newUpdatesSyncCompletedSubject.asObservable();
        this.eventService.newUpdatesSyncRequired$.pipe(concatMap(() => this.init())).subscribe();
    }

    /**
     * Only show those whatsnew updates for which notification is already present on the device, except if it is of an ADA message type
     * If another update having same notification Id, message and message type is already present in the local storage
     * then no need to show duplicate whatsnew messages.
     *
     * @returns an observable of the list of notification updates after syncing with the backend and local storage
     */
    public init(): Observable<NotificationUpdateModel[]> {
        this.notificationUpdates = [];
        const latestUpdates: NotificationUpdateModel[] = [];

        return this.notificationService.saveNotificationStatusModelList().pipe(
            mergeMap(() => from(this.localStorageNotificationUpdateService.getNotificationUpdatesAsync())),
            map((localActiveUpdates) => {
                //  get the filtered list of notification updates
                const allUpdates = [...latestUpdates, ...localActiveUpdates];
                this.notificationUpdates = _.uniqWith(allUpdates, (update1, update2) =>
                    NotificationUpdateModel.compare(update1, update2),
                );

                this.isInitialized = true;
                this.newUpdatesSyncCompletedSubject.next(this.notificationUpdates);
                return this.notificationUpdates;
            }),
        );
    }

    public checkIfCancellationExists(id: NotificationIdentifierModel): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `NotificationUpdateService: checkIfCancellationExists: Trying to call NotificationUpdateService before initialization!`,
            );
        }

        return !!this.getNotificationUpdateKeyByIdForCancellation(id);
    }

    public getNotificationUpdateKeyByIdForCancellation(id: NotificationIdentifierModel): string {
        if (!this.isInitialized) {
            throw new Error(
                `NotificationUpdateService: getNotificationUpdateKeyByIdForCancellation: Trying to call NotificationUpdateService before initialization!`,
            );
        }

        const update = this.notificationUpdates.find(
            (notificationUpdateModel) =>
                NotificationIdentifierModel.compare(notificationUpdateModel.notificationId, id) &&
                notificationUpdateModel.isMessageTypeCancelled(),
        );
        return update?.notificationUpdateKey ?? '';
    }

    public removeUpdate(notificationUpdateKey: string): void {
        if (!this.isInitialized) {
            throw new Error(
                `NotificationUpdateService: removeUpdate: Trying to call NotificationUpdateService before initialization!`,
            );
        }

        _.remove(this.notificationUpdates, (n) => n.notificationUpdateKey === notificationUpdateKey);
        this.newUpdatesSyncCompletedSubject.next(this.notificationUpdates);
        this.localStorageNotificationUpdateService
            .saveNotificationUpdates(this.notificationUpdates)
            .catch((error) => this.log.error(this.constructor.name, 'failed to remove update', error));
    }

    public removeNotificationUpdates(notificationIdentifier: NotificationIdentifier): void {
        if (!this.isInitialized) {
            throw new Error(
                `NotificationUpdateService: removeNotificationUpdates: Trying to call NotificationUpdateService before initialization!`,
            );
        }

        _.remove(this.notificationUpdates, (n) =>
            NotificationIdentifierModel.compare(notificationIdentifier, n.notificationId),
        );
        this.newUpdatesSyncCompletedSubject.next(this.notificationUpdates);
        this.localStorageNotificationUpdateService
            .saveNotificationUpdates(this.notificationUpdates)
            .catch((error) => this.log.error(this.constructor.name, 'failed to remove notification update', error));
    }

    /**
     * Called from init()
     * If a notification has whatsnew updates(ADA or material update) and then it gets cancelled,
     * the other updates should be removed in order to prevent further actions on their click
     * (eg. navigation to details screen and possibility of user sending ETA for cancelled notification should not be possible)
     *
     * @param notificationIdentifier
     */
    private removeNotificationUpdatesExceptCancellation(notificationIdentifier: NotificationIdentifierModel): void {
        _.remove(this.notificationUpdates, (n) => {
            return (
                !n.isMessageTypeCancelled() &&
                NotificationIdentifierModel.compare(notificationIdentifier, n.notificationId)
            );
        });
        this.newUpdatesSyncCompletedSubject.next(this.notificationUpdates);
        this.localStorageNotificationUpdateService
            .saveNotificationUpdates(this.notificationUpdates)
            .catch((error) =>
                this.log.error(
                    this.constructor.name,
                    'failed to remove notification updates except cancellation',
                    error,
                ),
            );
    }

    /**
     * Get the cancelled notification updates count
     */
    public getCancelledUpdatesCount(): number {
        if (!this.isInitialized) {
            throw new Error(
                `NotificationUpdateService: getCancelledUpdatesCount: Trying to call NotificationUpdateService before initialization!`,
            );
        }
        return this.notificationUpdates.filter((update) => update.isMessageTypeCancelled()).length;
    }

    /**
     * Called from init()
     * 1. If user is on details screen when the (not in-progress) notification got cancelled, then confirm cancellation popup will appear instantly,
     * to force user to confirm cancellation, and then route to dashboard
     * So that no further POST requests can be made from details screen for the cancelled notification.
     * 2. If user is on short confirmation screen when the notification got cancelled, then confirm cancellation popup will appear instantly,
     * to force user to confirm cancellation, and route to details screen.
     * So that short confirmation is not allowed for cancelled notification.
     * 3. If user is on normal confirmation screen when the notification got cancelled, then Confirmation-Equipment CommercialInfoText has to be updated with a default value
     * so confirm cancellation popup will appear instantly to force user to confirm cancellation, and route to details screen
     *
     * @param notification
     * @param key
     */
    private async preventDetailsScreenActivityAndShortCrm(notification: NotificationModel, key: string): Promise<void> {
        const isDetailsOrCrmOpen =
            this.router.url.indexOf(ConfigConstants.routeUrlDetails) > -1 ||
            this.router.url.indexOf(ConfigConstants.routeUrlConfirmation) > -1;

        if (
            this.routerHelper.params.frontid === notification.identifier.getFrontId() &&
            this.routerHelper.params.qmnum === notification.identifier.getQmnum() &&
            this.routerHelper.params.manum === notification.identifier.getManum() &&
            isDetailsOrCrmOpen
        ) {
            const model = await CancellationModel.create(notification, key);

            const cancellationModel = this.cancellationComponent as unknown as new (
                ...args: unknown[]
            ) => BaseCancellationComponent;
            const modalInstance = this.modalService.show(cancellationModel, {
                backdrop: 'static',
                initialState: {
                    model: model,
                    disableModalClose: true,
                },
                ariaLabelledBy: 'cancellation-modal-title',
            });

            (<CancellationComponentInterface>modalInstance.content).modalResult.subscribe(
                (result: { notificationUpdateKey: string }) => {
                    if (result && result.notificationUpdateKey) {
                        this.removeUpdate(result.notificationUpdateKey);
                    }
                    if (!this.notificationService.isNotificationInProgress(notification.identifier)) {
                        this.router.navigate([ConfigConstants.routeUrlDashboard]).catch((error: unknown) => {
                            throw new Error(
                                `NotificationUpdateService: preventDetailsScreenActivityAndShortCrm Could not change URL to dashboard: ${JSON.stringify(
                                    error,
                                )}`,
                            );
                        });
                    }
                },
            );
        }
    }
}
